import { useState } from 'react';
import { pulseSelfFixSet, pulseSelfFixes, type SelfFixesResponse } from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { sx } from '../../ui/designStyles';
import { pre, usePulseApi } from './lib';

/** The self-fix a pre-approved fix key names, as the incident row says it (fixLine, V4 L28536). */
export const FIX_PHRASE: Record<string, string> = {
  worker: 'recycle the runtime worker',
  cron: 'restart the skipped cron job',
  queue: 'retry the stuck notifications',
  cache: 'clear the stale cache',
  probe: 're-arm the dropped probe',
};

/** An incident's pre-approved fix line, or '' (Self-fixing is not built: AF-1589). */
export function fixLineOf(fixKey: string | null | undefined, resolved: boolean): string {
  if (!fixKey || !FIX_PHRASE[fixKey]) return '';
  return 'Pre-approved fix: ' + FIX_PHRASE[fixKey] + '.' + (resolved ? ' Self-fixing is not built yet (AF-1589); this one cleared on its own.' : ' Self-fixing is not built yet (AF-1589), so a person was paged.');
}

const GRID = 'display:grid;grid-template-columns:minmax(0,1.3fr) minmax(0,1.5fr) minmax(0,1.3fr) minmax(0,1fr) minmax(0,110px);gap:10px;';

/**
 * Self-fixes, pre-approved (Spec V4 14, design T8417): the five bounded fixes Pulse will run on
 * its own once AF-1589 ships, each with its trigger, limit and what happens if it fails. Only
 * Andrew turns one Allowed or Off (sealed to the Truth Ledger on the server); everyone else
 * reads. Nothing here runs a fix: the badge says so, from the server's built flag.
 */
export function SelfFixes() {
  const { client } = useSession();
  const q = usePulseApi((c, signal) => pulseSelfFixes(c, { signal }), []);
  const [err, setErr] = useState<string | null>(null);
  const d: SelfFixesResponse | null = q.data;
  const owner = !!d?.canEdit;
  const toggle = (key: string, allowed: boolean) => {
    if (!owner) return;
    setErr(null);
    pulseSelfFixSet(client, { params: { key }, body: { allowed } }).then(
      (r) => q.setData(() => r),
      (e: unknown) => setErr(e instanceof Error ? e.message : 'The change was not saved.'),
    );
  };
  return (
    <div data-screen-label="Pulse self-fixes" data-testid="pulse-self-fixes" style={sx('margin-top:18px;border-top:1px solid rgba(34,39,46,.14);padding-top:11px;')}>
      <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;')}>
        <span style={sx('width:7px;height:7px;border-radius:50%;background:#2F5C97;flex:0 0 7px;')} />
        <span style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;')}>Self-fixes · pre-approved</span>
        {(!d || !d.built) && (
          <span style={sx('display:inline-flex;align-items:center;height:22px;padding:0 6px;border-radius:4px;font-size:14px;font-weight:800;letter-spacing:.06em;white-space:nowrap;color:#54606C;background:rgba(34,39,46,.08);')}>
            {'NOT BUILT YET · ' + (d?.ticket ?? 'AF-1589')}
          </span>
        )}
        <span style={sx('font-size:16px;color:#54606C;flex:1 1 300px;text-wrap:pretty;')}>
          <span>{'Pulse will run these on its own and page a person only when one fails or isn’t allowed. ' + (owner ? 'You choose which are allowed.' : 'Only Andrew chooses which are allowed.')}</span>
        </span>
      </div>
      {q.error && !d && <div style={sx('font-size:14px;color:#54606C;margin-top:8px;')}>The self-fix list could not be loaded.</div>}
      {d && (
        <div style={sx('overflow-x:auto;margin-top:6px;')}>
          <div style={sx('min-width:820px;')}>
            <div style={sx(GRID + 'padding:6px 2px;font-size:14px;letter-spacing:.06em;text-transform:uppercase;color:#54606C;font-weight:600;')}>
              <span>Fix</span>
              <span>When it runs</span>
              <span>Limit</span>
              <span>{'If it doesn’t work'}</span>
              <span>Allowed</span>
            </div>
            {d.fixes.map((f) => (
              <div key={f.key} style={sx(GRID + 'align-items:center;padding:8px 2px;border-top:1px solid rgba(34,39,46,.1);font-size:16px;color:#3C454F;line-height:1.4;')}>
                <span style={sx('font-weight:700;color:#22272E;')}>
                  <span>{f.name}</span>
                </span>
                <span>
                  <span>{f.when}</span>
                </span>
                <span>
                  <span>{f.limit}</span>
                </span>
                <span>
                  <span>{f.fail}</span>
                </span>
                <span style={sx('display:flex;align-items:center;gap:8px;')}>
                  <a
                    href="#"
                    role="switch"
                    aria-checked={f.allowed}
                    aria-label={f.name}
                    aria-disabled={owner ? undefined : 'true'}
                    title={owner ? undefined : 'Only Andrew chooses which are allowed.'}
                    onClick={(e) => {
                      pre(e);
                      toggle(f.key, !f.allowed);
                    }}
                    style={sx('flex:0 0 34px;width:34px;height:20px;border-radius:10px;position:relative;display:block;' + (f.allowed ? 'background:#264C7E;' : 'background:rgba(34,39,46,.18);') + (owner ? '' : 'opacity:.55;cursor:default;'))}
                  >
                    <span style={sx('position:absolute;top:2px;left:' + (f.allowed ? 16 : 2) + 'px;width:16px;height:16px;border-radius:50%;background:#fff;box-shadow:0 1px 2px rgba(34,39,46,.25);')} />
                  </a>
                  <span style={sx('font-size:14px;color:#54606C;')}>
                    <span>{f.allowed ? 'Allowed' : 'Off'}</span>
                  </span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      {err && <div role="status" style={sx('font-size:14px;color:#B3261E;margin-top:6px;')}>{err}</div>}
      <div style={sx('display:flex;gap:6px 22px;flex-wrap:wrap;padding-top:8px;border-top:1px solid rgba(34,39,46,.1);font-size:14px;color:#54606C;line-height:1.5;')}>
        <span style={sx('flex:1 1 320px;text-wrap:pretty;')}>
          <span style={sx('font-weight:700;color:#3C454F;')}>Fix log:</span>
          {d && d.log.length ? ' ' + d.log.length + ' entries, sealed to the Truth Ledger.' : ' none yet. Each fix will log what it saw, what it did, and whether it worked, sealed to the Truth Ledger.'}
        </span>
        <span style={sx('flex:1 1 320px;text-wrap:pretty;')}>Never self-fixed: anything that changes care, alerts, or resident data. Those always page a person.</span>
      </div>
    </div>
  );
}
